
import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Tier1Tracking } from '@/services/analytics/Tier1Tracking';
import { ContentItem } from '@/components/dashboard/content-card/types';
import { fetchContentWithTags } from './useContentTags';
import { INBOX_ID } from '@/utils/contentFormUtils';

interface UseCollectionContentProps {
  collectionId?: string;
  isArchive?: boolean;
  selectedTagIds?: string[];
  tagLogicType?: "AND" | "OR";
}

export const useCollectionContent = ({
  collectionId,
  isArchive = false,
  selectedTagIds = [],
  tagLogicType = "AND"
}: UseCollectionContentProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  // Fetch content items for the collection
  const { data: contentItems = [], isLoading: isContentLoading, refetch } = useQuery({
    queryKey: ['collectionContent', collectionId, isArchive, selectedTagIds, tagLogicType],
    queryFn: async () => {
      if (!user) return [];

      // If we have selected tags, filter by tags
      if (selectedTagIds.length > 0) {
        console.log(`Fetching content with tags: ${selectedTagIds.join(', ')} using ${tagLogicType} logic`);
        const taggedContent = await fetchContentWithTags(user.id, selectedTagIds, tagLogicType);
        // Ensure proper type casting for tag-filtered content
        return taggedContent.map(item => ({
          ...item,
          metadata: (item.metadata as any) || {},
          pinned: false // Default pinned state for tagged content
        })) as ContentItem[];
      }

      // PRIORITY 1: Handle Archive - if isArchive is true, fetch archived content
      if (isArchive) {
        console.log("Fetching archived content");
        
        const { data, error } = await supabase
          .from('content_items')
          .select('*')
          .eq('user_id', user.id)
          .eq('archived', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching archived content:', error);
          return [];
        }

        console.log(`Found ${data?.length || 0} archived items`);

        // Fix the type mismatch by casting metadata properly
        return (data || []).map(item => ({
          ...item,
          metadata: (item.metadata as any) || {},
          pinned: false // Default pinned state for archived content
        })) as ContentItem[];
      }

      // PRIORITY 2: Handle the special "In Box" collection (virtual collection for unorganized content)
      if (collectionId === INBOX_ID || collectionId === "inbox") {
        console.log("Fetching In Box content (unorganized items)");
        
        // Get all content items that are not in any collection and not archived
        const { data, error } = await supabase
          .from('content_items')
          .select('*')
          .eq('user_id', user.id)
          .eq('archived', false)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching content items:', error);
          return [];
        }

        if (!data || data.length === 0) {
          return [];
        }

        // Get content IDs that are already in collections
        const { data: collectionContentIds, error: collectionError } = await supabase
          .from('content_collections')
          .select('content_id');

        if (collectionError) {
          console.error('Error fetching collection content IDs:', collectionError);
          return [];
        }

        // Filter out items that are in collections to get only unorganized content
        const organizedContentIds = new Set(
          collectionContentIds?.map(item => item.content_id) || []
        );

        const inboxContent = data.filter(item => !organizedContentIds.has(item.id));
        
        console.log(`Found ${inboxContent.length} items in In Box`);

        // Fix the type mismatch by casting metadata properly
        return inboxContent.map(item => ({
          ...item,
          metadata: (item.metadata as any) || {},
          pinned: false // Default pinned state for inbox content
        })) as ContentItem[];
      }

      // PRIORITY 3: Regular collection handling - FIXED TO READ PINNED STATE AND SORT CORRECTLY
      if (collectionId) {
        console.log(`Fetching content for collection: ${collectionId}`);
        
        // First get content items that belong to this collection WITH pinned status
        const { data: collectionItems, error: collectionError } = await supabase
          .from('content_collections')
          .select('content_id, pinned')
          .eq('collection_id', collectionId);

        if (collectionError) {
          console.error('Error fetching collection items:', collectionError);
          return [];
        }

        if (!collectionItems || collectionItems.length === 0) {
          console.log(`No items found in collection ${collectionId}`);
          return [];
        }

        // Get the content IDs and create a map for pinned status
        const contentIds = collectionItems.map(item => item.content_id);
        const pinnedMap = new Map(collectionItems.map(item => [item.content_id, item.pinned || false]));

        console.log(`[PIN DEBUG] Collection items pinned status:`, 
          collectionItems.map(item => `${item.content_id}: ${item.pinned}`).join(', ')
        );

        // Now fetch the actual content items
        const { data: contentData, error: contentError } = await supabase
          .from('content_items')
          .select('*')
          .eq('user_id', user.id)
          .eq('archived', isArchive)
          .in('id', contentIds);

        if (contentError) {
          console.error('Error fetching content data:', contentError);
          return [];
        }

        console.log(`Found ${contentData?.length || 0} content items for collection`);

        // Map the content with pinned status from the junction table
        const itemsWithPinned = (contentData || []).map(item => {
          const isPinned = pinnedMap.get(item.id) || false;
          console.log(`[PIN DEBUG] Item ${item.id} (${item.title}) pinned status: ${isPinned}`);
          
          return {
            ...item,
            metadata: (item.metadata as any) || {},
            pinned: isPinned
          };
        }) as ContentItem[];

        // Sort: pinned items first, then by creation date
        const sortedItems = itemsWithPinned.sort((a, b) => {
          // If one is pinned and the other isn't, pinned comes first
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;
          
          // If both have same pinned status, sort by creation date (newest first)
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });

        console.log(`[PIN DEBUG] Final sorted order:`, 
          sortedItems.map(item => `${item.title} (pinned: ${item.pinned})`).join(', ')
        );

        return sortedItems;
      }

      // Default: fetch all content for user
      const { data, error } = await supabase
        .from('content_items')
        .select('*')
        .eq('user_id', user.id)
        .eq('archived', isArchive)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching content:', error);
        return [];
      }

      // Fix the type mismatch by casting metadata properly
      return (data || []).map(item => ({
        ...item,
        metadata: (item.metadata as any) || {},
        pinned: false // Default pinned state
      })) as ContentItem[];
    },
    enabled: !!user,
  });

  // Pin content to top of collection
  const pinContent = useCallback(async (contentId: string) => {
    console.log(`[PIN DEBUG] pinContent called with contentId: ${contentId}, collectionId: ${collectionId}`);
    
    if (!user || !collectionId || !contentId) {
      console.log(`[PIN DEBUG] Missing required data: user=${!!user}, collectionId=${collectionId}, contentId=${contentId}`);
      toast.error('Cannot pin content: missing required information');
      return false;
    }
    
    setIsLoading(true);
    try {
      console.log(`[PIN DEBUG] Attempting to pin content ${contentId} in collection ${collectionId}`);
      
      const { error } = await supabase
        .from('content_collections')
        .update({ pinned: true })
        .eq('content_id', contentId)
        .eq('collection_id', collectionId);
      
      if (error) {
        console.error('[PIN DEBUG] Error pinning content:', error);
        toast.error('Failed to pin content');
        return false;
      }
      
      console.log(`[PIN DEBUG] Successfully pinned content ${contentId}`);
      
      // Invalidate and refetch queries
      queryClient.invalidateQueries({ queryKey: ['collectionContent', collectionId] });
      
      toast.success('Content pinned to top');
      return true;
    } catch (error) {
      console.error('[PIN DEBUG] Exception while pinning content:', error);
      toast.error('Failed to pin content');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, collectionId, queryClient]);

  // Unpin content from top of collection
  const unpinContent = useCallback(async (contentId: string) => {
    console.log(`[PIN DEBUG] unpinContent called with contentId: ${contentId}, collectionId: ${collectionId}`);
    
    if (!user || !collectionId || !contentId) {
      console.log(`[PIN DEBUG] Missing required data: user=${!!user}, collectionId=${collectionId}, contentId=${contentId}`);
      toast.error('Cannot unpin content: missing required information');
      return false;
    }
    
    setIsLoading(true);
    try {
      console.log(`[PIN DEBUG] Attempting to unpin content ${contentId} in collection ${collectionId}`);
      
      const { error } = await supabase
        .from('content_collections')
        .update({ pinned: false })
        .eq('content_id', contentId)
        .eq('collection_id', collectionId);
      
      if (error) {
        console.error('[PIN DEBUG] Error unpinning content:', error);
        toast.error('Failed to unpin content');
        return false;
      }
      
      console.log(`[PIN DEBUG] Successfully unpinned content ${contentId}`);
      
      // Invalidate and refetch queries
      queryClient.invalidateQueries({ queryKey: ['collectionContent', collectionId] });
      
      toast.success('Content unpinned');
      return true;
    } catch (error) {
      console.error('[PIN DEBUG] Exception while unpinning content:', error);
      toast.error('Failed to unpin content');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, collectionId, queryClient]);

  // Add content to collection
  const addContentToCollection = useCallback(async (contentId: string) => {
    if (!user || !collectionId || !contentId) return false;
    
    setIsLoading(true);
    try {
      console.log(`[COLLECTION ANALYTICS] === STARTING ADD TO COLLECTION PROCESS ===`);
      console.log(`[COLLECTION ANALYTICS] Adding content ${contentId} to collection ${collectionId}`);
      
      // Check if already in collection
      const { data: existing, error: checkError } = await supabase
        .from('content_collections')
        .select('*')
        .eq('content_id', contentId)
        .eq('collection_id', collectionId)
        .maybeSingle();
      
      if (checkError) {
        console.error('Error checking existing collection content:', checkError);
        toast.error('Failed to add to collection');
        return false;
      }
      
      if (existing) {
        toast.info('Content already in collection');
        return true;
      }
      
      const { error } = await supabase
        .from('content_collections')
        .insert([{ content_id: contentId, collection_id: collectionId }]);
      
      if (error) {
        console.error('Error adding content to collection:', error);
        toast.error('Failed to add to collection');
        return false;
      }
      
      console.log(`[COLLECTION ANALYTICS] === CONTENT ADDED TO DATABASE SUCCESSFULLY ===`);
      
      // ANALYTICS: Track content organization - added to collection
      try {
        console.log(`[COLLECTION ANALYTICS] About to call Tier1Tracking.trackContentOrganized...`);
        console.log(`[COLLECTION ANALYTICS] Parameters:`, {
          contentId,
          action: 'added_to_collection',
          targetId: collectionId,
          targetType: 'collection',
          user: user ? `${user.id} (${user.email})` : 'None'
        });
        
        await Tier1Tracking.trackContentOrganized(
          contentId,
          'added_to_collection',
          collectionId,
          'collection',
          user
        );
        console.log(`[COLLECTION ANALYTICS] === ANALYTICS TRACKING COMPLETED SUCCESSFULLY ===`);
      } catch (analyticsError) {
        console.error('[COLLECTION ANALYTICS] === ANALYTICS TRACKING FAILED ===');
        console.error('[COLLECTION ANALYTICS] Analytics error:', analyticsError);
        // Don't fail the whole operation if analytics fails
      }
      
      queryClient.invalidateQueries({ queryKey: ['collectionContent', collectionId] });
      toast.success('Added to collection successfully');
      
      console.log(`[COLLECTION ANALYTICS] === ADD TO COLLECTION PROCESS COMPLETED ===`);
      return true;
    } catch (error) {
      console.error('Error adding content to collection:', error);
      toast.error('Failed to add to collection');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, collectionId, queryClient]);

  // Remove content from collection
  const removeContentFromCollection = useCallback(async (contentId: string) => {
    if (!user || !collectionId || !contentId) return false;
    
    setIsLoading(true);
    try {
      console.log(`[COLLECTION ANALYTICS] === STARTING REMOVE FROM COLLECTION PROCESS ===`);
      console.log(`[COLLECTION ANALYTICS] Removing content ${contentId} from collection ${collectionId}`);
      
      const { error } = await supabase
        .from('content_collections')
        .delete()
        .eq('content_id', contentId)
        .eq('collection_id', collectionId);
      
      if (error) {
        console.error('Error removing content from collection:', error);
        toast.error('Failed to remove from collection');
        return false;
      }
      
      console.log(`[COLLECTION ANALYTICS] === CONTENT REMOVED FROM DATABASE SUCCESSFULLY ===`);
      
      // ANALYTICS: Track content organization - removed from collection
      try {
        console.log(`[COLLECTION ANALYTICS] About to call Tier1Tracking.trackContentOrganized for removal...`);
        console.log(`[COLLECTION ANALYTICS] Parameters:`, {
          contentId,
          action: 'removed_from_collection',
          targetId: collectionId,
          targetType: 'collection',
          user: user ? `${user.id} (${user.email})` : 'None'
        });
        
        await Tier1Tracking.trackContentOrganized(
          contentId,
          'removed_from_collection',
          collectionId,
          'collection',
          user
        );
        console.log(`[COLLECTION ANALYTICS] === ANALYTICS TRACKING COMPLETED SUCCESSFULLY ===`);
      } catch (analyticsError) {
        console.error('[COLLECTION ANALYTICS] === ANALYTICS TRACKING FAILED ===');
        console.error('[COLLECTION ANALYTICS] Analytics error:', analyticsError);
        // Don't fail the whole operation if analytics fails
      }
      
      queryClient.invalidateQueries({ queryKey: ['collectionContent', collectionId] });
      toast.success('Removed from collection successfully');
      
      console.log(`[COLLECTION ANALYTICS] === REMOVE FROM COLLECTION PROCESS COMPLETED ===`);
      return true;
    } catch (error) {
      console.error('Error removing content from collection:', error);
      toast.error('Failed to remove from collection');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, collectionId, queryClient]);

  return {
    contentItems,
    isLoading: isLoading || isContentLoading,
    refetch,
    addContentToCollection,
    removeContentFromCollection,
    pinContent,
    unpinContent
  };
};
